import type { PublicWhoPlays } from "../../lib/api";
import type { CurpNotice } from "../../lib/publicRegistrationMode";

/** «2014-05-21» → «21/05/2014». */
function shortDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}

/** Aviso de la CURP del jugador (Eduardo 2026-10-01). Fuerte cuando la CURP
 * parece de otra persona o del papá/mamá: no se toma de ella la fecha ni el
 * género hasta que la familia decide, y «Guardar datos» espera esa decisión.
 * Si el nombre coincide y solo la fecha es otra, lo dice así (un error al
 * escribir la fecha, no otra persona). Leve cuando el nombre solo se parece. */
export default function CurpCheckNotice({
  id,
  notice,
  whoPlays,
  onUseCurp,
  onUseDate,
  onUploadAnother,
  onAccept,
  onUseName,
  onKeepTyped,
}: {
  /** Para llevar ahí el foco cuando «Guardar datos» se detiene. */
  id?: string;
  notice: CurpNotice;
  whoPlays: PublicWhoPlays;
  onUseCurp: () => void;
  onUseDate: () => void;
  onUploadAnother: () => void;
  onAccept: () => void;
  onUseName: () => void;
  onKeepTyped: () => void;
}) {
  if (notice.kind === "birthdate") {
    return (
      <div id={id} tabIndex={-1} className="public-registration-curp-check is-date" role="status" data-no-toast>
        <p className="public-registration-curp-check__text">
          La fecha de nacimiento no coincide con la CURP ({shortDate(notice.date)}).
        </p>
        <div className="public-registration-curp-check__actions">
          <button className="button button--primary" type="button" onClick={onUseDate}>
            Usar la fecha de la CURP
          </button>
        </div>
        <button className="public-registration-curp-check__accept" type="button" onClick={onAccept}>
          La fecha está bien, continuar
        </button>
      </div>
    );
  }

  if (notice.kind === "similar") {
    return (
      <div id={id} tabIndex={-1} className="alert alert--warning public-registration-curp-review" role="status">
        <strong>Confirma el nombre antes de aplicarlo</strong>
        <ul>
          {notice.conflicts.map((item) => (
            <li key={item.label}>
              <strong>{item.label}:</strong> El documento dice “{item.suggested}” — tú escribiste “{item.typed}”.
            </li>
          ))}
        </ul>
        <div className="public-registration-curp-review__actions">
          <button className="button button--primary" type="button" onClick={onUseName}>
            Usar datos del documento
          </button>
          <button className="button button--secondary" type="button" onClick={onKeepTyped}>
            Conservar lo escrito
          </button>
        </div>
      </div>
    );
  }

  const ownCurp = notice.kind === "guardian_curp";
  const text = ownCurp
    ? `Esta CURP es tuya (${notice.name}). Sube la CURP de quien va a jugar.`
    : whoPlays === "child"
      ? `Esta CURP es de ${notice.name}. Escribe los datos del jugador, no los tuyos.`
      : `Esta CURP es de ${notice.name} y no coincide con el nombre que escribiste.`;
  return (
    <div id={id} tabIndex={-1} className="public-registration-curp-check" role="alert" data-no-toast>
      <p className="public-registration-curp-check__text">
        <span aria-hidden="true">⚠️ </span>
        {text}
      </p>
      <div className="public-registration-curp-check__actions">
        {notice.kind === "other_person" && notice.canUseCurp ? (
          <button className="button button--primary" type="button" onClick={onUseCurp}>
            Usar datos de la CURP
          </button>
        ) : null}
        <button className={`button ${ownCurp ? "button--primary" : "button--secondary"}`} type="button" onClick={onUploadAnother}>
          Subir otra CURP
        </button>
      </div>
      <button className="public-registration-curp-check__accept" type="button" onClick={onAccept}>
        El nombre está bien, continuar
      </button>
    </div>
  );
}
